import { Injectable, inject } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { NavigationEnd, Router, UrlTree } from '@angular/router';
import { LoadingController } from '@ionic/angular/standalone';
import { filter, take } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { StorageService } from '../../../core/services/storage.service';
import { AuthStateService } from '../../../core/services/auth-state.service';
import type { FieldJumpStep } from '../pages/survey-detail/survey-field-jump';
import type { GroupedChoices, RawField } from '../pages/survey-detail/survey-schema';
import {
  NAMEPLATE_MAX_DIMENSION,
  NAMEPLATE_QUALITY,
  PHOTO_MAX_DIMENSION,
  PHOTO_QUALITY,
} from './photo-settings';

/**
 * Where a capture was started, so it can be resumed after the app/WebView is killed while
 * the native camera is in front (Android 17 can kill our WebView renderer then; see
 * MainActivity's onRenderProcessGone handler).
 */
export interface CaptureCheckpoint {
  readonly v: 1;
  readonly userId: string;
  readonly companyId: string;
  readonly surveyId: string;
  readonly localDraftId: string;
  readonly submissionId: number;
  readonly tabName: string;
  /** 'tab' = inline field on the survey page; 'unit' = inside the unit / nested-repeat modals. */
  readonly origin: 'tab' | 'unit';
  /** Repeat hops from the root form to the field's row (same shape as the jump-to-field flow). */
  readonly path: readonly FieldJumpStep[];
  readonly fieldName: string;
  readonly isNameplate: boolean;
  /** Epoch ms; refreshed right before the native camera/gallery opens. */
  readonly startedAt: number;
  readonly attempts: number;
}

/** Survey-page hooks the service needs while a capture is in progress. */
export interface CaptureContext {
  readonly surveyId: string;
  readonly localDraftId: string;
  readonly submissionId: number;
  activeTabName(): string;
  /** Persist the draft to disk now (autoSaveData(true)). */
  flush(): Promise<void>;
  /** The loaded survey definition, saved so a restore doesn't have to download it again. */
  surveySnapshot(): SurveySnapshot | null;
}

/** What survey-detail's loadSurveyData() needs from the network, kept locally for a restore. */
export interface SurveySnapshot {
  readonly surveyId: string;
  readonly name: string;
  readonly integration: string;
  readonly fields: readonly RawField[];
  readonly choices: GroupedChoices;
}


const STORAGE_KEY = 'digimep.capture.pending';
const MAX_AGE_MS = 30 * 60 * 1000;
const MAX_ATTEMPTS = 2;
/** Clock/mtime slack when matching the camera's output file to the checkpoint. */
const FILE_TIME_SLACK_MS = 5000;
const RESTORE_OVERLAY_TIMEOUT_MS = 60_000;

/**
 * Capture checkpoint + recovery.
 *
 * Before leaving the app for the camera/gallery we flush the draft and store where the user
 * is (survey, draft, tab, unit → nested → sub-nested path, field). If the app comes back
 * cold, AppComponent routes back to that survey + draft, and the survey page reopens the same
 * modal chain and applies the photo the camera wrote to `files/Pictures/` (or the plugin's
 * restored result, when Capacitor managed to replay it).
 */
@Injectable({ providedIn: 'root' })
export class CaptureRecoveryService {
  private readonly storage = inject(StorageService);
  private readonly authState = inject(AuthStateService);
  private readonly router = inject(Router);

  private context: CaptureContext | null = null;
  /** This app session already counted its one resume attempt (guard or fallback). */
  private attemptCounted = false;
  /** Survey whose definition snapshot was already written this app session. */
  private snapshotSavedFor: string | null = null;
  private readonly loadingCtrl = inject(LoadingController);
  private overlay: Promise<HTMLIonLoadingElement> | null = null;
  private overlayTimer: ReturnType<typeof setTimeout> | null = null;
  /** webPath of a Camera result Capacitor replayed after the activity was recreated. */
  private restoredPhoto: string | null = null;

  constructor() {
    if (Capacitor.isNativePlatform()) {
      void App.addListener('appRestoredResult', (event) => {
        const data = event?.data as { webPath?: string; path?: string } | undefined;
        if (event?.pluginId === 'Camera' && event.success && (data?.webPath || data?.path)) {
          this.restoredPhoto = data.webPath ?? Capacitor.convertFileSrc(data.path as string);
        }
      });
    }
  }

  /** Called by the survey page once its draft identity is known (normal mode only). */
  setContext(context: CaptureContext | null): void {
    this.context = context;
  }

  /**
   * Record where a pick is starting. `rootForm`/`scope` locate the field's row; returns
   * false (nothing stored) when there is no draft to come back to.
   */
  begin(opts: {
    rootForm: FormGroup;
    scope: FormGroup;
    fieldName: string;
    origin: 'tab' | 'unit';
    isNameplate: boolean;
  }): boolean {
    const ctx = this.context;
    if (!ctx || !ctx.localDraftId || !ctx.submissionId) return false;
    const path = pathToScope(opts.rootForm, opts.scope);
    if (!path) return false;
    const checkpoint: CaptureCheckpoint = {
      v: 1,
      userId: String(this.authState.userProfile()?.id ?? ''),
      companyId: String(this.authState.selectedCompany()?.id ?? ''),
      surveyId: ctx.surveyId,
      localDraftId: ctx.localDraftId,
      submissionId: ctx.submissionId,
      tabName: ctx.activeTabName(),
      origin: opts.origin,
      path,
      fieldName: opts.fieldName,
      isNameplate: opts.isNameplate,
      startedAt: Date.now(),
      attempts: 0,
    };
    this.storage.set(STORAGE_KEY, checkpoint);
    return true;
  }

  /**
   * Right before the native camera/gallery opens: refresh the timestamp and flush the draft
   * so the latest edits (and, for a brand-new survey, its draft index entry) are on disk.
   */
  async beforeLeavingApp(): Promise<void> {
    const cp = this.read();
    if (!cp) return;
    this.storage.set(STORAGE_KEY, { ...cp, startedAt: Date.now() });
    try {
      await Promise.all([this.context?.flush(), this.saveSurveySnapshot()]);
    } catch {
      // Best effort — the 30 s autosave / a network reload are the fallbacks.
    }
  }

  /**
   * Keep the loaded survey definition on disk (once per survey per app session) so a restore
   * can rebuild the page without the survey/fields/choices downloads.
   */
  private async saveSurveySnapshot(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    const snapshot = this.context?.surveySnapshot();
    if (!snapshot || this.snapshotSavedFor === snapshot.surveyId) return;
    // IndexedDB, not a Capacitor file: the definition can be several MB, and pushing that
    // through the native bridge as one string took ~14 s to read back on the emulator.
    // IndexedDB stays inside the WebView (structured clone, no JSON) and survives restarts.
    const db = await openSnapshotDb();
    await idbRequest(
      db,
      'readwrite',
      (store) => {
        store.clear(); // keep only the survey currently being captured in
        return store.put(snapshot);
      },
    );
    db.close();
    this.snapshotSavedFor = snapshot.surveyId;
  }

  /** The saved survey definition for a pending restore of `surveyId`, or null (→ download). */
  async readSurveySnapshot(surveyId: string): Promise<SurveySnapshot | null> {
    if (!Capacitor.isNativePlatform()) return null;
    try {
      const db = await openSnapshotDb();
      const snapshot = await idbRequest<SurveySnapshot | undefined>(db, 'readonly', (store) => store.get(surveyId));
      db.close();
      return snapshot && Array.isArray(snapshot.fields) ? snapshot : null;
    } catch {
      return null;
    }
  }

  /** The pick finished in this session (applied, duplicate, cancelled) — nothing to recover. */
  clear(): void {
    this.storage.remove(STORAGE_KEY);
    this.restoredPhoto = null;
  }

  /** True when a recent checkpoint for the signed-in user is waiting to be resumed. */
  hasResumable(): boolean {
    const cp = this.read();
    return !!cp && this.isResumable(cp);
  }

  /**
   * For the startup redirect (guestGuard): the survey + draft URL to resume, so the first
   * navigation goes straight there instead of flashing /home. Counts one resume attempt.
   * Returns null (and drops a stale checkpoint) when there is nothing to resume.
   */
  resumeUrlTree(): UrlTree | null {
    const cp = this.read();
    if (!cp) return null;
    if (!this.isResumable(cp)) {
      this.clear();
      return null;
    }
    this.countAttempt(cp);
    return this.router.createUrlTree(['/surveys', cp.surveyId], {
      queryParams: { draft_id: cp.localDraftId, submission_id: cp.submissionId },
    });
  }

  /**
   * App startup fallback for when the guest-guard redirect didn't run (e.g. the app was
   * opened on another route): once the first navigation settles, go to the pending survey.
   */
  resumeIfPending(): void {
    const after = (): void => {
      const cp = this.read();
      if (!cp) return;
      if (!this.isResumable(cp, this.attemptCounted)) {
        this.clear();
        this.endRestoreOverlay();
        return;
      }
      if (this.router.url.startsWith(`/surveys/${cp.surveyId}`)) return;
      this.countAttempt(cp);
      void this.router.navigate(['/surveys', cp.surveyId], {
        queryParams: { draft_id: cp.localDraftId, submission_id: cp.submissionId },
        replaceUrl: true,
      });
    };
    if (this.router.navigated) {
      after();
      return;
    }
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd), take(1))
      .subscribe(after);
  }

  /**
   * The survey page shows the app's standard loader ("Restoring your photo…") while it reloads
   * the draft and finds the photo, until endRestoreOverlay(). A safety timeout only lifts the
   * loader; the restore itself still runs when the draft finishes loading.
   */
  startRestoreOverlay(): void {
    if (this.overlay) return;
    this.overlay = this.loadingCtrl
      .create({ message: 'Restoring your photo…', backdropDismiss: false })
      .then(async (loading) => {
        await loading.present();
        return loading;
      });
    this.overlayTimer = setTimeout(() => this.endRestoreOverlay(), RESTORE_OVERLAY_TIMEOUT_MS);
  }

  endRestoreOverlay(): void {
    if (this.overlayTimer) {
      clearTimeout(this.overlayTimer);
      this.overlayTimer = null;
    }
    const overlay = this.overlay;
    this.overlay = null;
    void overlay?.then((loading) => loading.dismiss()).catch(() => {});
  }

  private countAttempt(cp: CaptureCheckpoint): void {
    if (this.attemptCounted) return;
    this.attemptCounted = true;
    this.storage.set(STORAGE_KEY, { ...cp, attempts: cp.attempts + 1 });
  }

  /** The checkpoint for this survey page's draft, if one is waiting to be resumed. */
  pendingFor(surveyId: string, localDraftId: string): CaptureCheckpoint | null {
    const cp = this.read();
    if (!cp || cp.surveyId !== surveyId || cp.localDraftId !== localDraftId) return null;
    return this.isResumable(cp, true) ? cp : null;
  }

  /**
   * The photo taken for `cp`, as a local file URL (or a data URL if it had to be downscaled),
   * or null if none was found (e.g. the user cancelled in the camera).
   */
  async findCapturedPhoto(cp: CaptureCheckpoint): Promise<string | null> {
    if (this.restoredPhoto) return this.restoredPhoto;
    if (Capacitor.getPlatform() !== 'android') return null;
    try {
      // CameraUtils.createImageFile writes into getExternalFilesDir(DIRECTORY_PICTURES).
      const { files } = await Filesystem.readdir({ path: 'Pictures', directory: Directory.External });
      const candidates = files
        .filter((f) => f.type === 'file' && /^JPEG_.*\.jpe?g$/i.test(f.name) && f.size > 0)
        .filter((f) => Number(f.mtime) >= cp.startedAt - FILE_TIME_SLACK_MS)
        .sort((a, b) => Number(b.mtime) - Number(a.mtime));
      const newest = candidates[0];
      if (!newest) return null;
      return await this.limitSize(Capacitor.convertFileSrc(newest.uri), cp.isNameplate);
    } catch (e) {
      console.warn('[CaptureRecovery] could not look for the captured photo:', e);
      return null;
    }
  }

  /**
   * If the app died before the camera plugin resized the photo, the file on disk is the
   * full-size capture; downscale it once (EXIF orientation applied) so it matches what a
   * normal pick produces. Normal-size files are returned unchanged.
   */
  private async limitSize(url: string, nameplate: boolean): Promise<string | null> {
    const maxDimension = nameplate ? NAMEPLATE_MAX_DIMENSION : PHOTO_MAX_DIMENSION;
    const quality = (nameplate ? NAMEPLATE_QUALITY : PHOTO_QUALITY) / 100;
    const blob = await fetch(url).then((r) => (r.ok ? r.blob() : null));
    if (!blob) return null;
    // Read the size from the JPEG header first: the usual case (plugin already resized the
    // file) then needs no decode at all — decoding a raw 200 MP capture would cost ~800 MB.
    const size = jpegSize(new Uint8Array(await blob.slice(0, 256 * 1024).arrayBuffer()));
    if (size && size.width <= maxDimension && size.height <= maxDimension) return url;
    const bitmap = await createImageBitmap(blob, { imageOrientation: 'from-image' });
    try {
      const { width, height } = bitmap;
      if (width <= maxDimension && height <= maxDimension) return url;
      const scale = maxDimension / Math.max(width, height);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(width * scale);
      canvas.height = Math.round(height * scale);
      canvas.getContext('2d')?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      const out = canvas.toDataURL('image/jpeg', quality);
      canvas.width = 0;
      canvas.height = 0;
      return out;
    } finally {
      bitmap.close();
    }
  }

  private isResumable(cp: CaptureCheckpoint, allowLastAttempt = false): boolean {
    const maxAttempts = allowLastAttempt ? MAX_ATTEMPTS + 1 : MAX_ATTEMPTS;
    return (
      cp.v === 1 &&
      Date.now() - cp.startedAt <= MAX_AGE_MS &&
      cp.attempts < maxAttempts &&
      this.authState.isAuthenticated() &&
      cp.userId === String(this.authState.userProfile()?.id ?? '') &&
      cp.companyId === String(this.authState.selectedCompany()?.id ?? '')
    );
  }

  private read(): CaptureCheckpoint | null {
    const cp = this.storage.get<CaptureCheckpoint>(STORAGE_KEY);
    return cp && cp.v === 1 ? cp : null;
  }
}

/**
 * Repeat hops from `root` down to `target` (FormArray control name + row index at each
 * level). Groups are flattened in this app's forms, but nested FormGroups are walked too.
 * Returns [] when target is root, null when it isn't inside root.
 */
export function pathToScope(root: FormGroup, target: FormGroup): FieldJumpStep[] | null {
  if (root === target) return [];
  for (const [name, ctrl] of Object.entries(root.controls)) {
    if (ctrl instanceof FormArray) {
      for (let i = 0; i < ctrl.length; i++) {
        const row = ctrl.at(i);
        if (!(row instanceof FormGroup)) continue;
        const rest = pathToScope(row, target);
        if (rest) return [{ fieldName: name, index: i }, ...rest];
      }
    } else if (ctrl instanceof FormGroup) {
      const rest = pathToScope(ctrl, target);
      if (rest) return rest;
    }
  }
  return null;
}

/** Follow `path` from `root` to the row FormGroup it names, or null if a hop is missing. */
export function scopeAtPath(root: FormGroup, path: readonly FieldJumpStep[]): FormGroup | null {
  let scope: FormGroup = root;
  for (const step of path) {
    const arr = scope.get(step.fieldName);
    if (!(arr instanceof FormArray)) return null;
    const row = arr.at(step.index);
    if (!(row instanceof FormGroup)) return null;
    scope = row;
  }
  return scope;
}

/**
 * Pixel size from a JPEG's SOF header (first frame marker), without decoding the image.
 * Returns null when the bytes aren't a JPEG or the header isn't within `bytes`.
 */
export function jpegSize(bytes: Uint8Array): { width: number; height: number } | null {
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) return null;
  let i = 2;
  while (i + 9 < bytes.length) {
    if (bytes[i] !== 0xff) {
      i++;
      continue;
    }
    const marker = bytes[i + 1];
    // SOF0-SOF15 carry the frame size, except DHT (C4), JPG (C8) and DAC (CC).
    if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc) {
      const height = (bytes[i + 5] << 8) | bytes[i + 6];
      const width = (bytes[i + 7] << 8) | bytes[i + 8];
      return width > 0 && height > 0 ? { width, height } : null;
    }
    // Standalone markers have no length field.
    if (marker === 0xd8 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7) || marker === 0xff) {
      i += marker === 0xff ? 1 : 2;
      continue;
    }
    const length = (bytes[i + 2] << 8) | bytes[i + 3];
    i += 2 + length;
  }
  return null;
}

const SNAPSHOT_DB = 'digimep-capture-recovery';
const SNAPSHOT_STORE = 'surveys';

/** Open (creating on first use) the IndexedDB holding the survey-definition snapshot. */
function openSnapshotDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(SNAPSHOT_DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(SNAPSHOT_STORE, { keyPath: 'surveyId' });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** Run one request on the snapshot store and resolve with its result once the transaction commits. */
function idbRequest<T>(
  db: IDBDatabase,
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SNAPSHOT_STORE, mode);
    const req = run(tx.objectStore(SNAPSHOT_STORE));
    tx.oncomplete = () => resolve(req.result as T);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}
